import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, Loader2, Image as ImageIcon, Film, X, Check } from 'lucide-react';
import { api } from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const empty = { media_type: 'image', media_url: '', thumbnail_url: '', caption: '', tags: [], priority: 0, is_published: true };

const InspoPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item or empty when creating
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/inspo/items?limit=500`);
      const d = await res.json();
      setItems(d.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ ...empty }); setTagInput(''); setError(''); };
  const startEdit = (item) => { setEditing({ ...item }); setTagInput(''); setError(''); };
  const cancelEdit = () => { setEditing(null); setError(''); };

  const saveItem = async () => {
    setError(''); setBusy(true);
    try {
      const token = api.getToken();
      const isEditing = !!editing.id;
      const url = isEditing ? `${API_URL}/api/inspo/items/${editing.id}` : `${API_URL}/api/inspo/items`;
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editing),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      await load();
      setEditing(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete this inspo item? "${item.caption || item.id}"`)) return;
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/inspo/items/${item.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const isVideo = file.type.startsWith('video/');
      const folder = isVideo ? 'inspo-videos' : 'inspo-images';
      let url;
      if (isVideo) {
        // Use existing video upload flow (presigned)
        url = await api.uploadVideo(file, folder);
      } else {
        url = await api.uploadImage(file, folder);
      }
      const inferredType = isVideo ? 'video' : (file.type === 'image/gif' ? 'gif' : 'image');
      setEditing(prev => ({ ...prev, media_url: url, media_type: inferredType }));
    } catch (e) {
      setError(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t) return;
    if ((editing.tags || []).includes(t)) { setTagInput(''); return; }
    setEditing(prev => ({ ...prev, tags: [...(prev.tags || []), t] }));
    setTagInput('');
  };
  const removeTag = (t) => setEditing(prev => ({ ...prev, tags: (prev.tags || []).filter(x => x !== t) }));

  return (
    <div data-testid="inspo-admin-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Inspo Grid</h2>
          <p className="text-sm text-gray-500">Pinterest-style stills, clips and GIFs shown on the public /inspo page.</p>
        </div>
        <button
          type="button"
          onClick={startNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white text-sm hover:bg-black/80"
          data-testid="inspo-new-btn"
        >
          <Plus size={16} /> Add inspo item
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400 py-10 text-center">No inspo items yet — click &ldquo;Add inspo item&rdquo; to start.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="inspo-admin-grid">
          {items.map(item => (
            <div key={item.id} className="border border-gray-200 bg-white relative group">
              <div className="aspect-square bg-gray-50 overflow-hidden">
                {item.media_type === 'video' ? (
                  <video src={item.media_url} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={item.media_url} alt={item.caption} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="p-2 flex items-center justify-between">
                <p className="text-xs truncate flex-1">{item.caption || '(no caption)'}</p>
                <span className="text-[9px] uppercase tracking-wider text-gray-400 ml-2">{item.media_type}</span>
              </div>
              <div className="absolute inset-x-0 top-0 p-1 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-b from-black/50 to-transparent">
                <button type="button" onClick={() => startEdit(item)} className="p-1.5 bg-white rounded hover:bg-gray-100" title="Edit">
                  <Edit2 size={12} />
                </button>
                <button type="button" onClick={() => deleteItem(item)} className="p-1.5 bg-white rounded hover:bg-red-50 text-red-600" title="Delete">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) cancelEdit(); }}>
          <div className="bg-white max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="inspo-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editing.id ? 'Edit' : 'New'} inspo item</h3>
              <button type="button" onClick={cancelEdit} aria-label="Close" className="text-gray-400 hover:text-black"><X size={18} /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1">Upload image, GIF, or short video</label>
                <input type="file" accept="image/*,video/*" onChange={handleUpload} disabled={uploading} className="text-sm" />
                {uploading && <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Uploading...</p>}
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Or paste media URL</label>
                <input type="url" value={editing.media_url}
                       onChange={e => setEditing(prev => ({ ...prev, media_url: e.target.value }))}
                       className="w-full border rounded px-3 py-2 text-sm" placeholder="https://..." />
              </div>

              {editing.media_url && (
                <div className="border bg-gray-50 p-2">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Preview</p>
                  {editing.media_type === 'video' ? (
                    <video src={editing.media_url} className="w-full max-h-48 object-contain" controls muted />
                  ) : (
                    <img src={editing.media_url} alt="preview" className="w-full max-h-48 object-contain" />
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium mb-1">Media type</label>
                <div className="flex gap-2">
                  {['image', 'gif', 'video'].map(t => (
                    <button key={t} type="button"
                      onClick={() => setEditing(prev => ({ ...prev, media_type: t }))}
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${editing.media_type === t ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200'}`}>
                      {t === 'image' ? <><ImageIcon size={12} className="inline mr-1" />Image</> : t === 'gif' ? 'GIF' : <><Film size={12} className="inline mr-1" />Video</>}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Caption (optional)</label>
                <input type="text" value={editing.caption}
                       onChange={e => setEditing(prev => ({ ...prev, caption: e.target.value }))}
                       className="w-full border rounded px-3 py-2 text-sm" maxLength={200} />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Tags</label>
                <div className="flex gap-2 mb-2">
                  <input type="text" value={tagInput}
                         onChange={e => setTagInput(e.target.value)}
                         onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                         className="flex-1 border rounded px-3 py-2 text-sm" placeholder="e.g. 'golden hour', 'candid'" />
                  <button type="button" onClick={addTag} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-sm">Add</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(editing.tags || []).map(t => (
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-xs">
                      {t}
                      <button type="button" onClick={() => removeTag(t)} aria-label={`Remove ${t}`} className="text-gray-400 hover:text-red-600"><X size={10} /></button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.is_published}
                         onChange={e => setEditing(prev => ({ ...prev, is_published: e.target.checked }))} />
                  Published
                </label>
                <label className="flex items-center gap-2 text-sm">
                  Priority
                  <input type="number" value={editing.priority}
                         onChange={e => setEditing(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                         className="w-16 border rounded px-2 py-1 text-sm" />
                </label>
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <div className="flex gap-2 justify-end pt-2 border-t">
                <button type="button" onClick={cancelEdit} className="px-4 py-2 text-sm border border-gray-300 hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={saveItem} disabled={busy || !editing.media_url}
                  className="px-4 py-2 text-sm bg-black text-white hover:bg-black/80 disabled:bg-gray-300 inline-flex items-center gap-2"
                  data-testid="inspo-save-btn">
                  {busy ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Check size={14} /> Save</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InspoPanel;
